import { SuggestionsStrip } from "@/components/suggestions/SuggestionsStrip";
import { getSuggestionEventsSink, type RenderableSuggestion } from "@/domain/suggestions";
import { useOptimisticCart } from "@/hooks/use-optimistic-cart";
import { useDaypartSuggestions } from "@/hooks/use-suggestions";
import { buildSuggestionCartItem } from "@/lib/add-suggestion-to-cart";
import { getMerchantId } from "@/lib/company-storage";
import { daypartRowTitle, suggestionMessage } from "@/lib/i18n/suggestions-messages";
import { markSuggestionNavigation } from "@/lib/suggestion-navigation";
import { useSearchParams } from "react-router-dom";

const MIN_ITEMS_TO_SHOW = 2;

/**
 * "Mais pedidos agora" / band-specific title, home page. Placed near the
 * bestsellers rail; hidden below two renderable items, since a lone card
 * reads as an error rather than a row.
 */
export function DaypartSuggestionsRow({ cartItemIds }: { cartItemIds: string[] }) {
  const merchantId = getMerchantId();
  const { addItem } = useOptimisticCart();
  const [, setSearchParams] = useSearchParams();

  const { band, suggestions, enabled } = useDaypartSuggestions(cartItemIds);

  if (!enabled || suggestions.length < MIN_ITEMS_TO_SHOW) return null;

  const title = band ? daypartRowTitle(band) : suggestionMessage("daypartFallbackTitle");

  function handleAdd(suggestion: RenderableSuggestion) {
    addItem(buildSuggestionCartItem(suggestion, "DAYPART"));
    if (merchantId) {
      getSuggestionEventsSink(merchantId).enqueue({
        type: "ADD",
        surface: "DAYPART",
        itemIds: [suggestion.merchantItemId],
        arm: suggestion.arm,
        anchorItemId: suggestion.anchorItemId,
        items: [{ itemId: suggestion.merchantItemId, arm: suggestion.arm, anchorItemId: suggestion.anchorItemId }],
      });
    }
  }

  function openItem(itemOfferId: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("item", itemOfferId);
      return next;
    });
  }

  return (
    <SuggestionsStrip
      merchantId={merchantId}
      surface="DAYPART"
      title={title}
      suggestions={suggestions}
      combo={null}
      onAdd={handleAdd}
      onOpenDetail={(suggestion) => {
        markSuggestionNavigation(suggestion.itemOfferId, "DAYPART", suggestion.arm, suggestion.anchorItemId);
        openItem(suggestion.itemOfferId);
      }}
      onOpenCombo={openItem}
    />
  );
}
